/**
 * AirQualityLayer Component
 *
 * Displays air quality data as a smooth interpolated heatmap overlay on the map.
 * Uses PM2.5 and US AQI data from Open-Meteo CAMS model.
 * Only shows areas with Moderate or worse AQI (>50) to avoid cluttering the map.
 *
 * Data source: Open-Meteo Air Quality API (CAMS global model)
 * Update frequency: Every 5 minutes (via Lambda ingestion)
 * Coverage: Australia-wide (25x25 grid = 625 points)
 * Cost: Free (data pre-fetched by Lambda, served via CloudFront)
 *
 * AQI Color Scale (US EPA):
 * - 0-50: Good (hidden - not shown)
 * - 51-100: Moderate (yellow)
 * - 101-150: Unhealthy for Sensitive Groups (orange)
 * - 151-200: Unhealthy (red)
 * - 201-300: Very Unhealthy (purple)
 * - 301+: Hazardous (maroon)
 */

import { useEffect, useState, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

// Air quality JSON URL - served from CloudFront
const AIR_QUALITY_URL = import.meta.env.VITE_AIR_QUALITY_URL || '';

// Refresh interval - matches ingestion (5 minutes)
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Only show areas with AQI above this threshold (51+ = Moderate or worse)
const AQI_DISPLAY_THRESHOLD = 50;

interface AirQualityPoint {
  lat: number;
  lon: number;
  pm25: number | null;
  pm10: number | null;
  aqi: number | null;
}

interface AirQualityData {
  data: AirQualityPoint[];
  gridSize: number;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  timestamp: string;
  source: string;
  avg_pm25: number;
  avg_pm10: number;
  avg_aqi: number;
}

interface AirQualityLayerProps {
  enabled?: boolean;
  opacity?: number;
}

/**
 * Get RGB color components for AQI value using US EPA scale.
 */
function getAQIColorRGB(aqi: number): { r: number; g: number; b: number } {
  // Moderate (51-100) - yellow
  if (aqi <= 100) return { r: 255, g: 255, b: 0 };
  // USG (101-150) - orange
  if (aqi <= 150) return { r: 255, g: 126, b: 0 };
  // Unhealthy (151-200) - red
  if (aqi <= 200) return { r: 255, g: 0, b: 0 };
  // Very Unhealthy (201-300) - purple
  if (aqi <= 300) return { r: 143, g: 63, b: 151 };
  // Hazardous (301+) - maroon
  return { r: 126, g: 0, b: 35 };
}

/**
 * Inverse Distance Weighting interpolation for AQI values.
 * Returns interpolated AQI at a given point based on nearby grid points.
 */
function interpolateAQI(
  lat: number,
  lon: number,
  data: AirQualityPoint[],
  power: number = 2
): number | null {
  let sumWeightedValues = 0;
  let sumWeights = 0;

  for (const point of data) {
    if (point.aqi === null) continue;

    const distance = Math.sqrt(
      Math.pow(point.lat - lat, 2) + Math.pow(point.lon - lon, 2)
    );

    // If very close to a data point, use its value directly
    if (distance < 0.01) {
      return point.aqi;
    }

    const weight = 1 / Math.pow(distance, power);
    sumWeightedValues += point.aqi * weight;
    sumWeights += weight;
  }

  if (sumWeights === 0) return null;
  return sumWeightedValues / sumWeights;
}

/**
 * Get AQI category label.
 * Exported for potential use in tooltips or legends.
 */
export function getAQICategory(aqi: number | null): string {
  if (aqi === null) return 'No data';
  if (aqi <= 50) return 'Good';
  if (aqi <= 100) return 'Moderate';
  if (aqi <= 150) return 'Unhealthy for Sensitive Groups';
  if (aqi <= 200) return 'Unhealthy';
  if (aqi <= 300) return 'Very Unhealthy';
  return 'Hazardous';
}

/**
 * AirQualityLayer - displays air quality heatmap on the map.
 * Renders colored circles at grid points with interpolated coloring.
 */
export default function AirQualityLayer({ enabled = true, opacity = 0.4 }: AirQualityLayerProps) {
  const map = useMap();
  const [airQualityData, setAirQualityData] = useState<AirQualityData | null>(null);
  const canvasLayerRef = useRef<L.Layer | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Track the map center at the time of last render for smooth panning
  const renderCenterRef = useRef<L.LatLng | null>(null);

  // Fetch air quality data
  useEffect(() => {
    if (!enabled || !AIR_QUALITY_URL) {
      setAirQualityData(null);
      return;
    }

    const fetchAirQuality = async () => {
      try {
        const response = await fetch(AIR_QUALITY_URL, {
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch air quality: ${response.status}`);
        }

        const data: AirQualityData = await response.json();
        setAirQualityData(data);
        console.log(`[AirQualityLayer] Loaded ${data.data.length} points, avg AQI=${data.avg_aqi}`);
      } catch (err) {
        console.error('[AirQualityLayer] Error fetching air quality:', err);
      }
    };

    // Initial fetch
    fetchAirQuality();

    // Set up refresh interval
    const interval = setInterval(fetchAirQuality, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled]);

  // Create/update canvas overlay
  useEffect(() => {
    if (!enabled || !airQualityData) {
      // Remove canvas layer when disabled
      if (canvasLayerRef.current) {
        map.removeLayer(canvasLayerRef.current);
        canvasLayerRef.current = null;
      }
      if (canvasRef.current) {
        canvasRef.current.remove();
        canvasRef.current = null;
      }
      return;
    }

    // Create canvas if it doesn't exist
    if (!canvasRef.current) {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none';
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.zIndex = '400'; // Below markers but above base tiles
      // GPU acceleration hints for smooth panning
      canvas.style.willChange = 'transform';
      canvas.style.transform = 'translate(0px, 0px)';
      canvasRef.current = canvas;
      map.getContainer().appendChild(canvas);
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderAirQuality = () => {
      const size = map.getSize();
      const dpr = window.devicePixelRatio || 1;

      // Set canvas size
      canvas.width = size.x * dpr;
      canvas.height = size.y * dpr;
      canvas.style.width = `${size.x}px`;
      canvas.style.height = `${size.y}px`;
      ctx.scale(dpr, dpr);

      // Clear canvas
      ctx.clearRect(0, 0, size.x, size.y);

      // Store the map center at render time for smooth panning
      renderCenterRef.current = map.getCenter();
      // Reset any transform applied during panning
      canvas.style.transform = 'translate(0px, 0px)';

      const { bounds } = airQualityData;

      // Calculate pixel step for smooth rendering
      // Use smaller step for smoother gradients (every 4 pixels)
      const pixelStep = 4;

      // Create image data for smooth rendering
      const imageData = ctx.createImageData(size.x, size.y);
      const data = imageData.data;

      // Iterate over pixels
      for (let y = 0; y < size.y; y += pixelStep) {
        for (let x = 0; x < size.x; x += pixelStep) {
          // Convert pixel to lat/lon
          const point = map.containerPointToLatLng([x, y]);
          const lat = point.lat;
          const lon = point.lng;

          // Skip if outside data bounds
          if (lat < bounds.south || lat > bounds.north ||
              lon < bounds.west || lon > bounds.east) {
            continue;
          }

          // Interpolate AQI at this point
          const aqi = interpolateAQI(lat, lon, airQualityData.data);

          // Skip if no data or below threshold (Good AQI)
          if (aqi === null || aqi <= AQI_DISPLAY_THRESHOLD) {
            continue;
          }

          // Get color for this AQI value
          const color = getAQIColorRGB(aqi);

          // Calculate opacity based on how far above threshold
          // Fade in from threshold to create smooth edges
          const fadeRange = 20; // AQI units to fade in
          const fadeOpacity = Math.min(1, (aqi - AQI_DISPLAY_THRESHOLD) / fadeRange);
          const finalOpacity = opacity * fadeOpacity;

          // Fill the pixel block
          for (let dy = 0; dy < pixelStep && y + dy < size.y; dy++) {
            for (let dx = 0; dx < pixelStep && x + dx < size.x; dx++) {
              const idx = ((y + dy) * size.x + (x + dx)) * 4;
              data[idx] = color.r;
              data[idx + 1] = color.g;
              data[idx + 2] = color.b;
              data[idx + 3] = Math.round(finalOpacity * 255);
            }
          }
        }
      }

      // Put the image data onto the canvas
      ctx.putImageData(imageData, 0, 0);
    };

    // Handle smooth panning - apply CSS transform to move canvas with map
    const onMove = () => {
      if (!renderCenterRef.current || !canvas) return;

      // Get current center and the center at render time
      const currentCenter = map.getCenter();
      const renderCenter = renderCenterRef.current;

      // Convert both to pixel positions
      const currentPixel = map.latLngToContainerPoint(currentCenter);
      const renderPixel = map.latLngToContainerPoint(renderCenter);

      // Calculate offset (how much the map has moved since last render)
      const offsetX = renderPixel.x - currentPixel.x;
      const offsetY = renderPixel.y - currentPixel.y;

      // Apply transform to move canvas with the map
      canvas.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    };

    // Initial render
    renderAirQuality();

    // Re-render on map move end (resets transform and re-renders)
    const onMoveEnd = () => renderAirQuality();
    const onZoomEnd = () => renderAirQuality();
    const onResize = () => renderAirQuality();

    // Listen for move events (fires during panning)
    map.on('move', onMove);
    map.on('moveend', onMoveEnd);
    map.on('zoomend', onZoomEnd);
    map.on('resize', onResize);

    return () => {
      map.off('move', onMove);
      map.off('moveend', onMoveEnd);
      map.off('zoomend', onZoomEnd);
      map.off('resize', onResize);
    };
  }, [enabled, airQualityData, map, opacity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (canvasRef.current) {
        canvasRef.current.remove();
        canvasRef.current = null;
      }
    };
  }, []);

  // Update opacity - trigger re-render
  useEffect(() => {
    if (canvasRef.current && airQualityData) {
      // Trigger re-render with new opacity by firing resize event
      map.fire('resize');
    }
  }, [opacity, map, airQualityData]);

  return null;
}
